import React from 'react';
import {
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Button,
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
  Avatar,
  Input,
} from '@nextui-org/react';
import { VerticalDotsIcon } from '../../assets/icons/VerticalDotsIcon';
import { SearchIcon } from '../../assets/icons/SearchIcon';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import AddNewUserModal from '../modals/AddNewUserModal';
import EditRolesModal from '../modals/EditUserSAFFRolesModal';
import { User as ApiUser } from 'src/types/api/UserList';
import { useFetchUsersSAFF, useUserActions } from 'src/hooks/useUsersListHooks';
import { useEditRolesModalStore, useUserListStore } from 'src/store/api/useUserListStore';
import { useSAFFEmpStore } from 'src/store/layout/useSAFFEmpStore';
import PaginationWrapper from '../common/PaginationWrapper';

export default function UsersSAFF() {
  const { t } = useTranslation();
  const { loading } = useFetchUsersSAFF();
  const {
    handleSearchChangeUsersSAFF,
    handleIdSaffSearchChange,
    handleIdSaffSearch,
    setPageUsersSAFF,
    pageUsersSAFF,
    rowsPerPageUsersSAFF,
    usersSAFF,
    totalPagesUsersSAFF,
    totalUsersUsersSAFF,
    idSaffSearch,
  } = useUserActions();

  const { isAddUserModalOpen, setIsAddUserModalOpen, filterValue, setFilterValue, selectedUser, setSelectedUser } = useSAFFEmpStore();

  const { isOpen: isEditRolesModalOpen, setIsOpen: setEditRolesModalOpen } = useEditRolesModalStore();
  const { assignRolesToUser } = useUserListStore(); // Access the assignRolesToUser function from the store

  const handleEditRoles = (user: ApiUser) => {
    console.log(user.role_id);
    if (user.user_id) {
      setSelectedUser(user);
      setEditRolesModalOpen(true);
    } else {
      console.error('User ID is undefined');
    }
  };

  const handleSaveRoles = async (roles: string[]) => {
    if (selectedUser?.user_id) {
      await assignRolesToUser(selectedUser.user_id.toString(), roles);
    }
  };

  if (loading) return <div>{t('Loading')}</div>;

  const filteredUsers = usersSAFF.filter(user => {
    const searchTerm = filterValue.toLowerCase();
    
    // For clubs (is_club === 1)
    if (user.is_club === 1) {
      return (
        (user.club_name_en || '').toLowerCase().includes(searchTerm) ||
        (user.club_name_ar || '').toLowerCase().includes(searchTerm) ||
        (user.person_name_en || '').toLowerCase().includes(searchTerm) ||
        (user.person_name_ar || '').toLowerCase().includes(searchTerm) ||
        (user.username || '').toLowerCase().includes(searchTerm)
      );
    }
    
    // For individuals (is_club === 0)
    return (
      (user.full_name_en || '').toLowerCase().includes(searchTerm) ||
      (user.full_name_ar || '').toLowerCase().includes(searchTerm) ||
      (user.email || '').toLowerCase().includes(searchTerm) ||
      (user.department_en || '').toLowerCase().includes(searchTerm) ||
      (user.department_ar || '').toLowerCase().includes(searchTerm) ||
      (user.job_en || '').toLowerCase().includes(searchTerm) ||
      (user.job_ar || '').toLowerCase().includes(searchTerm)
    );
  });
  const paginatedUsers = filteredUsers;

  return (
    <div className="w-full p-4">
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-4">
          {/* Search Section */}
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-end">
            {/* Name Search */}
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('SearchByName')}
              </label>
              <Input
                isClearable
                classNames={{
                  base: 'w-full',
                  inputWrapper: 'border-1 bg-white',
                }}
                placeholder={t('SearchByName')}
                startContent={<SearchIcon className="text-default-300" />}
                value={filterValue}
                variant="bordered"
                onClear={() => setFilterValue('')}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setFilterValue(e.target.value);
                  handleSearchChangeUsersSAFF(e.target.value);
                }}
              />
            </div>
            
            {/* ID SAFF Search */}
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Search by ID SAFF
              </label>
              <div className="flex gap-2">
                <Input
                  isClearable
                  classNames={{
                    base: 'flex-1',
                    inputWrapper: 'border-1 bg-white',
                  }}
                  placeholder="Enter ID SAFF (e.g., 172)"
                  startContent={<SearchIcon className="text-default-300" />}
                  value={idSaffSearch}
                  variant="bordered"
                  onClear={() => handleIdSaffSearchChange('')}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    handleIdSaffSearchChange(e.target.value);
                  }}
                />
                <Button
                  color="primary"
                  variant="solid"
                  onPress={() => {
                    console.log('Search button clicked with ID SAFF:', idSaffSearch);
                    handleIdSaffSearch(); // Reset to page 1
                    // Trigger the search by calling fetchUsersSAFF directly
                    const { fetchUsersSAFF, pageUsersSAFF, rowsPerPageUsersSAFF } = useUserListStore.getState();
                    fetchUsersSAFF(pageUsersSAFF, rowsPerPageUsersSAFF, idSaffSearch);
                  }}
                  className="px-6"
                  isDisabled={!idSaffSearch.trim()}
                >
                  Search
                </Button>
              </div>
            </div>
          </div>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-default-400 text-small">
            {t('TotalUsers')}: {totalUsersUsersSAFF}
          </span>
          {/* <div className="flex items-center">
            <label className="mr-2 text-default-400 text-small">
              {t('RowsPerPage')}
            </label>
            <select
              className="border border-default-200 rounded px-2 py-1"
              value={rowsPerPageUsersSAFF}
              onChange={e => handleRowsPerPageChangeUsersSAFF(Number(e.target.value))}
            >
              {[5, 10, 15, 50, 100].map(size => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </div> */}
        </div>
      </div>
      <div className="flex flex-wrap gap-6 justify-start items-center mt-4">
        {paginatedUsers.map((user, index) => (
          <Card className="lg:w-[23%] w-[20rem] hover:scale-105" key={`user-${user.user_id || user.id || index}`}>
            <CardHeader className="justify-end">
              <Dropdown>
                <DropdownTrigger>
                  <Button isIconOnly radius="full" size="sm" variant="light">
                    <VerticalDotsIcon className="text-default-400" />
                  </Button>
                </DropdownTrigger>
                <DropdownMenu>
                  <DropdownItem key={user.user_id || ''} onClick={() => handleEditRoles(user)}>
                    {t('EditRoles')}
                  </DropdownItem>
                </DropdownMenu>
              </Dropdown>
            </CardHeader>
            <Link to="">
              <CardBody className="px-3 py-0 text-small text-center text-default-400">
                <div className="flex justify-center items-center">
                  <Avatar showFallback src="https://images.unsplash.com/broken" />
                </div>
                <span className="pt-2 capitalize text-gray-900 text-lg font-bold">
                  {user.is_club === 1 
                    ? (user.club_name_en || user.club_name_ar || user.username || 'No Club Name')
                    : (user.full_name_en || user.full_name_ar || user.person_name_en || user.person_name_ar || 'No Name')
                  }
                </span>
                <span className="pt-2 capitalize text-gray-900 text-sm font-medium">
                  ID SAFF: {user.user_id_saff || 'External'}
                </span>
                <span className="pt-2 capitalize text-gray-500 text-sm">
                  {user.is_club === 1 
                    ? (user.person_name_en || user.person_name_ar || 'Club Representative')
                    : (user.department_en || user.department_ar || user.department || user.job_en || user.job_ar || user.job || 'No Department')
                  }
                </span>
              </CardBody>
              <CardFooter className="gap-3"></CardFooter>
            </Link>
          </Card>
        ))}
      </div>
      <PaginationWrapper
        currentPage={pageUsersSAFF}
        totalItems={totalUsersUsersSAFF}
        itemsPerPage={rowsPerPageUsersSAFF}
        onPageChange={setPageUsersSAFF}
      />
      <AddNewUserModal
        isOpen={isAddUserModalOpen}
        onClose={() => setIsAddUserModalOpen(false)}
      />
      <EditRolesModal
        isOpen={isEditRolesModalOpen}
        onClose={() => setEditRolesModalOpen(false)}
        onSave={handleSaveRoles}
        user={selectedUser} // Pass the selected user object to the modal
      />
    </div>
  );
}